import { Shield, CheckCircle, AlertTriangle, Calendar, TrendingUp } from 'lucide-react';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { complianceDashboardData } from '@/data/mockData';

export const ComplianceDashboard: React.FC = () => {
  const { overallScore, activeControls, openFindings, upcomingAudits, topRisks, recentAudits, recentActivity, complianceByFramework } = complianceDashboardData;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'text-danger-500 bg-danger-500/20 border-danger-500/30';
      case 'High':
        return 'text-warning-500 bg-warning-500/20 border-warning-500/30';
      case 'Medium':
        return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
      case 'Low':
        return 'text-success-400 bg-success-500/20 border-success-500/30';
      default:
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-success-400';
    if (score >= 80) return 'text-primary-400';
    if (score >= 70) return 'text-warning-400';
    return 'text-danger-400';
  };

  return (
    <div className="space-y-6">
      <ComingSoonWatermark 
        pageKey="compliance-dashboard"
        icon={Shield}
        title="Compliance Dashboard Coming Soon"
        description="Comprehensive compliance monitoring dashboard with framework tracking, audit management, and regulatory reporting will be available soon."
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Compliance Dashboard</h1>
          <p className="text-neutral-400 mt-1">Overview of compliance posture and regulatory frameworks</p>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Overall Score</p>
              <p className={`text-3xl font-bold mt-2 ${getScoreColor(overallScore)}`}>
                {overallScore}%
              </p>
            </div>
            <div className="bg-primary-500/20 p-3 rounded-lg">
              <Shield className="w-6 h-6 text-primary-400" />
            </div>
          </div>
          <div className="flex items-center mt-4 text-sm">
            <TrendingUp className="w-4 h-4 text-success-400 mr-1" />
            <span className="text-success-400">+3% from last month</span>
          </div>
        </div>

        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Active Controls</p>
              <p className="text-3xl font-bold text-white mt-2">{activeControls}</p>
            </div>
            <div className="bg-success-500/20 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-success-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            Across all frameworks
          </div>
        </div>

        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Open Findings</p>
              <p className="text-3xl font-bold text-warning-400 mt-2">{openFindings}</p>
            </div>
            <div className="bg-warning-500/20 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-warning-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            Require remediation
          </div>
        </div>

        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Upcoming Audits</p>
              <p className="text-3xl font-bold text-primary-400 mt-2">{upcomingAudits}</p>
            </div>
            <div className="bg-primary-500/20 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-primary-400" />
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-400">
            Next 90 days
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Risks */}
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 text-warning-400 mr-2" />
            Top Risks
          </h2>
          <div className="space-y-3">
            {topRisks.map((risk) => (
              <div 
                key={risk.id} 
                className="flex items-start justify-between p-3 rounded-lg bg-neutral-900 hover:bg-neutral-800 transition-colors cursor-pointer"
                onClick={() => console.log('View risk:', risk.id)}
              >
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-neutral-500 text-sm mr-2">{risk.id}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityColor(risk.severity)}`}>
                      {risk.severity}
                    </span>
                  </div>
                  <p className="text-white text-sm mt-1">{risk.title}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="text-lg font-bold text-warning-400">{risk.score}</p>
                  <p className="text-xs text-neutral-500">Score</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance by Framework */}
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Shield className="w-5 h-5 text-primary-400 mr-2" />
            Compliance by Framework
          </h2>
          <div className="space-y-4">
            {complianceByFramework.map((framework) => (
              <div key={framework.framework} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{framework.framework}</p>
                    <p className="text-xs text-neutral-400">
                      {framework.controls_compliant}/{framework.controls_total} controls compliant
                    </p>
                  </div>
                  <span className={`text-lg font-bold ${getScoreColor(framework.score)}`}>
                    {framework.score}%
                  </span>
                </div>
                <div className="w-full bg-neutral-800 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-primary-500 to-cyan-500 h-2 rounded-full transition-all"
                    style={{ width: `${framework.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Audits */}
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
            <Calendar className="w-5 h-5 text-primary-400 mr-2" />
            Recent Audits
          </h2>
          <div className="space-y-3">
            {recentAudits.map((audit) => (
              <div 
                key={audit.id} 
                className="p-4 rounded-lg bg-neutral-900 hover:bg-neutral-800 transition-colors cursor-pointer"
                onClick={() => console.log('View audit:', audit.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-white font-medium">{audit.title}</p>
                    <p className="text-xs text-neutral-400 mt-1">{audit.date}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${
                    audit.status === 'Completed' 
                      ? 'text-success-400 bg-success-500/20 border-success-500/30'
                      : 'text-primary-400 bg-primary-500/20 border-primary-500/30'
                  }`}>
                    {audit.status}
                  </span>
                </div>
                {audit.outcome && (
                  <p className="text-sm text-neutral-300">{audit.outcome}</p>
                )}
                {audit.scope && !audit.outcome && (
                  <p className="text-sm text-neutral-400">{audit.scope}</p>
                )}
                {audit.findings !== undefined && (
                  <p className="text-xs text-neutral-500 mt-2">
                    {audit.findings} finding{audit.findings !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div 
                key={activity.id} 
                className="flex items-start p-3 rounded-lg hover:bg-neutral-900 transition-colors cursor-pointer"
                onClick={() => console.log('View activity:', activity.id)}
              >
                <div className="flex-1">
                  <p className="text-white text-sm">
                    <span className="font-medium">{activity.user}</span>
                    {' '}
                    <span className="text-neutral-400">{activity.action}</span>
                  </p>
                  <p className="text-primary-400 text-sm mt-1">{activity.resource}</p>
                  <p className="text-xs text-neutral-500 mt-1">{activity.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
